<script>
    // Licensed under Gnu Public Licence version 2
    // Copyright (c) 2020 (original work) Open Assessment Technologies SA ;
    import { createEventDispatcher } from 'svelte';
    import { tabFocus } from '@oat-sa-private/ui-core';
    import { IconBarButton, Icon } from '@oat-sa-private/ui-elements';

    const dispatch = createEventDispatcher();

    // min input width, rem
    const minSize = 20;

    export let name;
    export let value;
    export let placeholder;
    export let size;
    export let maxlength;

    let focusFromKey = false; // class toggler instead of :focus-visible to prevent conflict with native

    $: showClear = !!value;
    $: width = size ? `${Math.max(size, minSize)}rem` : '100%';

    /**
     * Handles `input` event
     * @fires {svelte event} change
     */
    function handleInput() {
        dispatch('change', value);
    }

    /**
     * Handles click on clear button
     * @fires {svelte event} change
     */
    function handleClear() {
        value = '';
        dispatch('change', value);
    }
</script>

<style>
    label {
        --input-width: auto;
        --input-height: 5.5rem;
        --input-padding: calc(var(--input-height) + var(--space-half));
        --search-icon-top-offset: 1rem;
        --search-icon-left-offset: 2rem;

        position: relative;
        display: flex;
        width: var(--input-width);

        & .search-icon,
        & .clear-icon {
            position: absolute;
            width: fit-content;
            z-index: var(--layer-1);
        }

        & .search-icon {
            top: var(--search-icon-top-offset);
            inset-inline-start: var(--search-icon-left-offset);
        }

        & .clear-icon {
            inset-inline-start: calc(100% - var(--input-height));
        }

        &:hover input {
            border-bottom: var(--border-medium) solid var(--color-border-actionable-hover);
        }

        & input {
            font-family: var(--font-ui);
            font-size: var(--fontsize-body);
            padding-left: var(--input-padding);
            padding-right: var(--input-padding);
            width: 100%;
            height: calc(var(--input-height) + var(--border-medium));
            border: none;
            border-bottom: var(--border-medium) solid var(--color-border-default);

            &:hover,
            &:focus,
            &:active {
                outline: none;
                border-bottom: var(--border-medium) solid var(--color-border-actionable-hover);
            }

            &.key-focus-visible {
                @add-mixin simple-outline;
            }
        }
    }
</style>

<label style="--input-width:{width};">
    <div class="search-icon">
        <Icon size="base-16" name="search-16" />
    </div>
    <input
        {name}
        bind:value
        {placeholder}
        {maxlength}
        use:tabFocus
        on:input={handleInput}
        on:tabfocus={() => (focusFromKey = true)}
        on:blur={() => (focusFromKey = false)}
        class:key-focus-visible={focusFromKey} />
    {#if showClear}
        <div class="clear-icon">
            <IconBarButton label="Clear" size="base-16" icon="remove-16" on:click={handleClear} />
        </div>
    {/if}
</label>
